import React, { useState, useEffect, useRef, useCallback } from "react";
import { useForAuthorisation } from "./context";
import { firestore, storage } from "./firebase";
import {
  collection,
  addDoc,
  onSnapshot,
  query,
  orderBy,
  deleteDoc,
  doc,
  updateDoc,
  serverTimestamp,
  where,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import "./CSS/CustomerChat.css";
import Navigation from "./navigation";

const CustomerChat = () => {
  const { loggedInUser, name, emailDetails } = useForAuthorisation();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [groups, setGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Group creation state
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupDescription, setNewGroupDescription] = useState("");
  const [isPrivateGroup, setIsPrivateGroup] = useState(false);
  const [groupMembers, setGroupMembers] = useState([]);
  const [searchEmail, setSearchEmail] = useState("");

  // Debug logging
  console.log("CustomerChat component loaded");
  console.log("loggedInUser:", loggedInUser);
  console.log("groups:", groups);
  console.log("activeGroup:", activeGroup);
  console.log("showCreateGroup:", showCreateGroup);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Function to create default groups (run once)
  const createDefaultGroups = async () => {
    try {
      const defaultGroups = [
        {
          name: "General Discussion",
          description:
            "Open discussion for all platform users - share ideas, ask questions, and connect!",
          isPrivate: false,
          createdBy: "system",
          createdAt: serverTimestamp(),
          members: [],
          admins: ["system"],
        },
        {
          name: "Business Networking",
          description:
            "Connect with other business owners, share opportunities, and grow your network.",
          isPrivate: false,
          createdBy: "system",
          createdAt: serverTimestamp(),
          members: [],
          admins: ["system"],
        },
        {
          name: "Customer Support",
          description:
            "Get help with the platform, report issues, and receive updates from our team.",
          isPrivate: false,
          createdBy: "system",
          createdAt: serverTimestamp(),
          members: [],
          admins: ["system"],
        },
        {
          name: "Marketing Tips & Strategies",
          description:
            "Share marketing insights, discuss strategies, and learn from other business owners.",
          isPrivate: false,
          createdBy: "system",
          createdAt: serverTimestamp(),
          members: [],
          admins: ["system"],
        },
      ];

      for (const group of defaultGroups) {
        await addDoc(collection(firestore, "chatGroups"), group);
      }

      console.log("Default chat groups created successfully!");
      alert("Default chat groups created! Refresh the page to see them.");
    } catch (error) {
      console.error("Error creating default groups:", error);
      alert("Error creating groups. Check console for details.");
    }
  };

  const loadGroups = useCallback(() => {
    if (!loggedInUser?.uid) return;

    console.log("Loading groups for user:", loggedInUser.uid);

    try {
      const groupsQuery = query(
        collection(firestore, "chatGroups"),
        where("members", "array-contains", loggedInUser.uid)
      );

      const publicGroupsQuery = query(
        collection(firestore, "chatGroups"),
        where("isPrivate", "==", false)
      );

      const unsubscribeGroups = onSnapshot(groupsQuery, (snapshot) => {
        const userGroups = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Get public groups
        const unsubscribePublicGroups = onSnapshot(
          publicGroupsQuery,
          (publicSnapshot) => {
            const publicGroups = publicSnapshot.docs
              .map((doc) => ({
                id: doc.id,
                ...doc.data(),
              }))
              .filter((group) => !userGroups.some((g) => g.id === group.id));

            const allGroups = [...userGroups, ...publicGroups];
            console.log("Groups loaded:", allGroups.length);
            setGroups(allGroups);

            // Set first group as active only on initial load
            setActiveGroup((prevActiveGroup) => {
              if (!prevActiveGroup && allGroups.length > 0) {
                console.log("Setting active group to:", allGroups[0].name);
                return allGroups[0];
              }
              return prevActiveGroup;
            });
          }
        );

        return () => {
          unsubscribePublicGroups();
        };
      });

      return () => {
        unsubscribeGroups();
      };
    } catch (error) {
      console.error("Error loading groups:", error);
    }
  }, [loggedInUser?.uid]);

  const loadMessages = useCallback(() => {
    if (!activeGroup) return;

    console.log("Loading messages for group:", activeGroup.id);

    // Using orderBy - Firebase will prompt to create index if needed
    const messagesQuery = query(
      collection(firestore, "chatMessages"),
      where("groupId", "==", activeGroup.id),
      orderBy("timestamp", "asc")
    );

    const unsubscribe = onSnapshot(
      messagesQuery,
      (snapshot) => {
        console.log(
          "Messages snapshot received, docs count:",
          snapshot.docs.length
        );
        const messagesData = snapshot.docs.map((doc) => {
          const data = doc.data();
          console.log("Message doc data:", data);
          return {
            id: doc.id,
            ...data,
          };
        });

        console.log("Messages data:", messagesData);
        setMessages(messagesData);
      },
      (error) => {
        console.error("Error in messages listener:", error);
        // Check if it's an index error
        if (
          error.code === "failed-precondition" &&
          error.message.includes("index")
        ) {
          console.log("🔥 FIRESTORE INDEX NEEDED:");
          console.log("Go to Firebase Console > Firestore Database > Indexes");
          console.log(
            "Or click the link in the error message above to create the index automatically"
          );

          // Fallback to query without orderBy
          console.log("Falling back to unordered query...");
          const fallbackQuery = query(
            collection(firestore, "chatMessages"),
            where("groupId", "==", activeGroup.id)
          );

          const fallbackUnsubscribe = onSnapshot(fallbackQuery, (snapshot) => {
            const messagesData = snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));

            // Sort on client side
            messagesData.sort((a, b) => {
              if (!a.timestamp || !b.timestamp) return 0;
              return a.timestamp.toMillis() - b.timestamp.toMillis();
            });

            setMessages(messagesData);
          });

          return fallbackUnsubscribe;
        }
      }
    );

    return unsubscribe;
  }, [activeGroup]);

  useEffect(() => {
    if (loggedInUser) {
      const unsubscribe = loadGroups();
      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
      };
    }
  }, [loggedInUser, loadGroups]);

  useEffect(() => {
    console.log("useEffect triggered - activeGroup:", activeGroup);
    if (activeGroup) {
      console.log("Loading messages for active group:", activeGroup.id);
      const unsubscribe = loadMessages();
      return () => {
        console.log("Cleaning up messages listener");
        if (unsubscribe) {
          unsubscribe();
        }
      };
    }
  }, [activeGroup, loadMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const createGroup = async () => {
    if (!newGroupName.trim() || !loggedInUser) return;

    try {
      const groupData = {
        name: newGroupName,
        description: newGroupDescription,
        isPrivate: isPrivateGroup,
        createdBy: loggedInUser.uid,
        createdAt: serverTimestamp(),
        members: [loggedInUser.uid, ...groupMembers],
        admins: [loggedInUser.uid],
      };

      await addDoc(collection(firestore, "chatGroups"), groupData);
      setShowCreateGroup(false);
      setNewGroupName("");
      setNewGroupDescription("");
      setIsPrivateGroup(false);
      setGroupMembers([]);
    } catch (error) {
      console.error("Error creating group:", error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() && attachments.length === 0) return;
    if (!activeGroup || !loggedInUser) return;

    console.log("Sending message:", newMessage);
    console.log("Active group:", activeGroup);
    console.log("Logged in user:", loggedInUser);

    setIsLoading(true);
    try {
      const attachmentUrls = await uploadAttachments();

      const messageData = {
        text: newMessage,
        groupId: activeGroup.id,
        senderId: loggedInUser.uid,
        senderName: name || emailDetails,
        senderEmail: emailDetails,
        timestamp: serverTimestamp(),
        attachments: attachmentUrls,
        replyTo: replyingTo,
        edited: false,
        editedAt: null,
      };

      console.log("Message data:", messageData);

      if (editingMessage) {
        await updateDoc(doc(firestore, "chatMessages", editingMessage.id), {
          text: newMessage,
          edited: true,
          editedAt: serverTimestamp(),
        });
        setEditingMessage(null);
        console.log("Message updated successfully");
      } else {
        const docRef = await addDoc(
          collection(firestore, "chatMessages"),
          messageData
        );
        console.log("Message sent successfully with ID:", docRef.id);
      }

      setNewMessage("");
      setAttachments([]);
      setReplyingTo(null);
    } catch (error) {
      console.error("Error sending message:", error);
    }
    setIsLoading(false);
  };

  const uploadAttachments = async () => {
    const uploadPromises = attachments.map(async (file) => {
      const storageRef = ref(
        storage,
        `chat-attachments/${Date.now()}-${file.name}`
      );
      const snapshot = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snapshot.ref);
      return {
        name: file.name,
        url: url,
        type: file.type,
        size: file.size,
      };
    });

    return await Promise.all(uploadPromises);
  };

  const deleteMessage = async (messageId) => {
    try {
      await deleteDoc(doc(firestore, "chatMessages", messageId));
    } catch (error) {
      console.error("Error deleting message:", error);
    }
  };

  const editMessage = (message) => {
    setEditingMessage(message);
    setNewMessage(message.text);
  };

  const replyToMessage = (message) => {
    setReplyingTo(message);
  };

  const joinGroup = async (groupId) => {
    try {
      const groupRef = doc(firestore, "chatGroups", groupId);
      await updateDoc(groupRef, {
        members: arrayUnion(loggedInUser.uid),
      });
    } catch (error) {
      console.error("Error joining group:", error);
    }
  };

  const leaveGroup = async (groupId) => {
    try {
      const groupRef = doc(firestore, "chatGroups", groupId);
      await updateDoc(groupRef, {
        members: arrayRemove(loggedInUser.uid),
      });
    } catch (error) {
      console.error("Error leaving group:", error);
    }
  };

  const removeMember = async (groupId, memberId) => {
    try {
      const groupRef = doc(firestore, "chatGroups", groupId);
      await updateDoc(groupRef, {
        members: arrayRemove(memberId),
      });
    } catch (error) {
      console.error("Error removing member:", error);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setAttachments((prev) => [...prev, ...files]);
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate();
    return date.toLocaleString();
  };

  const isAdmin = (group) => {
    return group.admins && group.admins.includes(loggedInUser?.uid);
  };

  const isMember = (group) => {
    return group.members && group.members.includes(loggedInUser?.uid);
  };

  if (!loggedInUser) {
    return (
      <div className="chat-container">
        <div className="chat-login-prompt">
          <h3>Please sign in to access the chat</h3>
          <p>
            Join the conversation and connect with businesses and customers!
          </p>
        </div>
      </div>
    );
  }

  // Loading state while checking authentication
  if (loggedInUser === undefined) {
    return (
      <div className="chat-container">
        <div className="chat-login-prompt">
          <h3>Loading...</h3>
          <p>Initializing chat system...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navigation stylingOnClickCustomerChat="MediumBlue" />
      <div className="chat-container">
        <div className="chat-sidebar">
          <div className="chat-header">
            <h3>Business Chat</h3>
            <button
              className="create-group-btn"
              onClick={() => {
                console.log("New Group button clicked");
                setShowCreateGroup(true);
              }}
            >
              + New Group
            </button>
            {groups.length === 0 && (
              <button
                className="create-group-btn"
                style={{
                  marginTop: "10px",
                  background: "linear-gradient(45deg, #00b894, #00a085)",
                }}
                onClick={createDefaultGroups}
              >
                Setup Default Groups
              </button>
            )}
          </div>

          <div className="groups-list">
            {groups.map((group) => (
              <div
                key={group.id}
                className={`group-item ${
                  activeGroup?.id === group.id ? "active" : ""
                }`}
                onClick={() => setActiveGroup(group)}
              >
                <div className="group-info">
                  <h4>{group.name}</h4>
                  <p>{group.description}</p>
                  <span className="group-type">
                    {group.isPrivate ? "🔒 Private" : "🌐 Public"}
                  </span>
                </div>
                {!isMember(group) && !group.isPrivate && (
                  <button
                    className="join-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      joinGroup(group.id);
                    }}
                  >
                    Join
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="chat-main">
          {activeGroup ? (
            <>
              <div className="chat-messages">
                <div className="group-header">
                  <h3>{activeGroup.name}</h3>
                  <div className="group-actions">
                    {isMember(activeGroup) && (
                      <button
                        className="leave-btn"
                        onClick={() => leaveGroup(activeGroup.id)}
                      >
                        Leave Group
                      </button>
                    )}
                  </div>
                </div>

                <div className="messages-container">
                  {messages.map((message) => (
                    <div key={message.id} className="message">
                      {message.replyTo && (
                        <div className="reply-reference">
                          <small>
                            Replying to: {message.replyTo.senderName}
                          </small>
                          <p>{message.replyTo.text.substring(0, 50)}...</p>
                        </div>
                      )}

                      <div className="message-header">
                        <strong>{message.senderName}</strong>
                        <span className="timestamp">
                          {formatTimestamp(message.timestamp)}
                        </span>
                        {message.edited && (
                          <span className="edited-tag">(edited)</span>
                        )}
                      </div>

                      <div className="message-content">
                        <p>{message.text}</p>

                        {message.attachments &&
                          message.attachments.length > 0 && (
                            <div className="attachments">
                              {message.attachments.map((attachment, index) => (
                                <div key={index} className="attachment">
                                  {attachment.type.startsWith("image/") ? (
                                    <img
                                      src={attachment.url}
                                      alt={attachment.name}
                                      className="attachment-image"
                                    />
                                  ) : attachment.type.startsWith("video/") ? (
                                    <video
                                      src={attachment.url}
                                      controls
                                      className="attachment-video"
                                    />
                                  ) : (
                                    <a
                                      href={attachment.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="attachment-file"
                                    >
                                      📎 {attachment.name}
                                    </a>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                      </div>

                      <div className="message-actions">
                        <button onClick={() => replyToMessage(message)}>
                          Reply
                        </button>
                        {message.senderId === loggedInUser.uid && (
                          <>
                            <button onClick={() => editMessage(message)}>
                              Edit
                            </button>
                            <button onClick={() => deleteMessage(message.id)}>
                              Delete
                            </button>
                          </>
                        )}
                        {isAdmin(activeGroup) &&
                          message.senderId !== loggedInUser.uid && (
                            <>
                              <button onClick={() => deleteMessage(message.id)}>
                                Delete
                              </button>
                              <button
                                onClick={() =>
                                  removeMember(activeGroup.id, message.senderId)
                                }
                              >
                                Remove User
                              </button>
                            </>
                          )}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </div>

              {/* Message Input */}
              <div className="message-input-container">
                {replyingTo && (
                  <div className="reply-preview">
                    <span>
                      Replying to {replyingTo.senderName}:{" "}
                      {replyingTo.text.substring(0, 50)}...
                    </span>
                    <button onClick={() => setReplyingTo(null)}>×</button>
                  </div>
                )}

                {editingMessage && (
                  <div className="edit-preview">
                    <span>Editing message</span>
                    <button
                      onClick={() => {
                        setEditingMessage(null);
                        setNewMessage("");
                      }}
                    >
                      ×
                    </button>
                  </div>
                )}

                {attachments.length > 0 && (
                  <div className="attachments-preview">
                    {attachments.map((file, index) => (
                      <div key={index} className="attachment-preview">
                        <span>{file.name}</span>
                        <button onClick={() => removeAttachment(index)}>
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="input-row">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                    disabled={isLoading}
                  />
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    multiple
                    accept="image/*,video/*,.pdf,.doc,.docx,.txt"
                    style={{ display: "none" }}
                  />
                  <button
                    className="attach-btn"
                    onClick={() => fileInputRef.current.click()}
                  >
                    📎
                  </button>
                  <button
                    className="send-btn"
                    onClick={sendMessage}
                    disabled={
                      isLoading ||
                      (!newMessage.trim() && attachments.length === 0)
                    }
                  >
                    Send
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="no-group-selected">
              <h3>Select a group to start chatting</h3>
              <p>
                Join public groups or create your own to connect with other
                users!
              </p>
            </div>
          )}
        </div>

        {/* Create Group Modal */}
        {showCreateGroup && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
            onClick={() => setShowCreateGroup(false)}
          >
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "10px",
                padding: "20px",
                width: "90%",
                maxWidth: "500px",
                maxHeight: "80vh",
                overflow: "auto",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <h3 style={{ margin: 0 }}>Create New Group</h3>
                <button
                  onClick={() => setShowCreateGroup(false)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "24px",
                    cursor: "pointer",
                  }}
                >
                  ×
                </button>
              </div>
              <div>
                <input
                  type="text"
                  placeholder="Group Name"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px",
                    marginBottom: "10px",
                    borderRadius: "5px",
                    border: "1px solid #ccc",
                  }}
                />
                <textarea
                  placeholder="Group Description"
                  value={newGroupDescription}
                  onChange={(e) => setNewGroupDescription(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px",
                    marginBottom: "10px",
                    borderRadius: "5px",
                    border: "1px solid #ccc",
                    minHeight: "80px",
                  }}
                />
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    margin: "10px 0",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isPrivateGroup}
                    onChange={(e) => setIsPrivateGroup(e.target.checked)}
                    style={{ marginRight: "10px" }}
                  />
                  Private Group (invite only)
                </label>

                {isPrivateGroup && (
                  <div
                    style={{ borderTop: "1px solid #eee", paddingTop: "10px" }}
                  >
                    <input
                      type="email"
                      placeholder="Enter email to invite"
                      value={searchEmail}
                      onChange={(e) => setSearchEmail(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "10px",
                        marginBottom: "10px",
                        borderRadius: "5px",
                        border: "1px solid #ccc",
                      }}
                    />
                    <button
                      onClick={() => {
                        if (
                          searchEmail &&
                          !groupMembers.includes(searchEmail)
                        ) {
                          setGroupMembers([...groupMembers, searchEmail]);
                          setSearchEmail("");
                        }
                      }}
                      style={{
                        padding: "8px 16px",
                        background: "#007bff",
                        color: "white",
                        border: "none",
                        borderRadius: "5px",
                        cursor: "pointer",
                        marginBottom: "10px",
                      }}
                    >
                      Add Member
                    </button>
                    <div
                      style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}
                    >
                      {groupMembers.map((email, index) => (
                        <span
                          key={index}
                          style={{
                            background: "#007bff",
                            color: "white",
                            padding: "5px 10px",
                            borderRadius: "15px",
                            fontSize: "14px",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                        >
                          {email}
                          <button
                            onClick={() =>
                              setGroupMembers((prev) =>
                                prev.filter((m) => m !== email)
                              )
                            }
                            style={{
                              background: "none",
                              border: "none",
                              color: "white",
                              cursor: "pointer",
                            }}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                  marginTop: "20px",
                }}
              >
                <button
                  onClick={() => setShowCreateGroup(false)}
                  style={{
                    padding: "10px 20px",
                    background: "#6c757d",
                    color: "white",
                    border: "none",
                    borderRadius: "5px",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={createGroup}
                  style={{
                    padding: "10px 20px",
                    background: "#007bff",
                    color: "white",
                    border: "none",
                    borderRadius: "5px",
                    cursor: "pointer",
                  }}
                >
                  Create Group
                </button>
              </div>
            </div>
          </div>
        )}
      </div>{" "}
    </div>
  );
};

export default CustomerChat;
